import React from 'react';

export type ChequeLeafArchiveProps = {
  bankAccountLabel: string;
  branchName?: string;
  serie: string;
  leafDisplayFa: string;
  fullName: string;
  amountFormatted: string;
  dueDate: string;
  amountInWords: string;
  nationalCode: string;
  description: string;
  sealDataUrl?: string;
  signatureDataUrl?: string;
  /** متن کوچک مثلاً «نسخه بایگانی» */
  footerNote?: string;
};

/**
 * نمای ثابت برگه چک برای چاپ بایگانی؛ همان فضای گرادیانی شبیه تعریف دسته‌چک
 */
const ChequeLeafArchive: React.FC<ChequeLeafArchiveProps> = ({
  bankAccountLabel,
  branchName,
  serie,
  leafDisplayFa,
  fullName,
  amountFormatted,
  dueDate,
  amountInWords,
  nationalCode,
  description,
  sealDataUrl,
  signatureDataUrl,
  footerNote,
}) => {
  return (
    <div
      className="rounded-xl border border-violet-200/80 bg-gradient-to-br from-violet-50 via-rose-50 to-indigo-100 p-3 shadow-md sm:p-4 print:border-slate-400 print:bg-white print:shadow-none"
      dir="rtl"
    >
      <div
        className="relative overflow-hidden rounded-sm border-[3px] border-slate-800 bg-gradient-to-br from-white/95 via-violet-50/40 to-rose-50/50 shadow-inner print:border-black"
        style={{ aspectRatio: '2.15 / 1', minHeight: '220px' }}
      >
        <div className="pointer-events-none absolute inset-0 border border-slate-600/35 m-1.5 rounded-sm print:border-slate-500" />
        <div className="relative flex h-full min-h-[200px] flex-col p-3 text-slate-900 sm:p-4 md:p-5">
          <div className="flex flex-wrap items-start justify-between gap-2 border-b border-dashed border-slate-400 pb-2 print:border-slate-600">
            <div className="min-w-0 text-right">
              <div className="text-[10px] text-slate-500 sm:text-xs">حساب / بانک</div>
              <div className="text-xs font-bold leading-tight sm:text-sm md:text-base">{bankAccountLabel}</div>
              {branchName ? (
                <div className="mt-0.5 text-[10px] text-slate-600 sm:text-xs">شعبه: {branchName}</div>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1 text-left" dir="ltr">
              <div className="flex items-center gap-2 font-mono text-xs text-slate-800 sm:text-sm">
                <span className="text-slate-500">سری</span>
                <span className="rounded border border-slate-400 bg-white px-2 py-0.5">{serie}</span>
              </div>
              <div className="flex items-center gap-2 font-mono text-xs text-slate-800 sm:text-sm">
                <span className="text-slate-500">شماره</span>
                <span className="rounded border-2 border-slate-800 bg-white px-3 py-0.5 font-semibold tracking-widest">
                  {leafDisplayFa}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-2 flex min-h-0 flex-1 flex-col gap-2 sm:gap-3">
            <div>
              <span className="text-[10px] text-slate-600 sm:text-xs">در وجه</span>
              <div className="border-b border-slate-800 py-1 text-sm font-medium sm:text-base">{fullName || '—'}</div>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
              <div>
                <span className="text-[10px] text-slate-600 sm:text-xs">مبلغ به عدد (ریال)</span>
                <div dir="ltr" className="border-b border-slate-800 py-1 text-end font-mono text-sm sm:text-base">
                  {amountFormatted || '—'}
                </div>
              </div>
              <div>
                <span className="text-[10px] text-slate-600 sm:text-xs">تاریخ سررسید</span>
                <div className="border-b border-slate-800 py-1 text-sm">{dueDate || '—'}</div>
              </div>
            </div>
            <div className="min-h-0 flex-1">
              <span className="text-[10px] text-slate-600 sm:text-xs">مبلغ به حروف</span>
              <div className="mt-0.5 min-h-[2.5rem] rounded border border-dashed border-slate-500 bg-white/90 p-2 text-sm leading-relaxed sm:text-base">
                {amountInWords || '—'}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 border-t border-dashed border-slate-400 pt-2 sm:grid-cols-12 print:border-slate-600">
              <div className="sm:col-span-4">
                <span className="text-[10px] text-slate-600 sm:text-xs">کد ملی</span>
                <div dir="ltr" className="border-b border-slate-800 py-1 font-mono text-sm">
                  {nationalCode || '—'}
                </div>
              </div>
              <div className="sm:col-span-5">
                <span className="text-[10px] text-slate-600 sm:text-xs">شرح</span>
                <div className="border-b border-slate-800 py-1 text-sm">{description || '—'}</div>
              </div>
              <div className="relative flex flex-col items-center justify-end sm:col-span-3">
                <div className="mb-1 text-[10px] text-slate-500">مهر و امضا</div>
                <div className="flex h-14 w-full max-w-[10rem] items-center justify-center gap-1 print:h-16">
                  {sealDataUrl ? (
                    <img src={sealDataUrl} alt="" className="max-h-12 max-w-[3.5rem] object-contain opacity-90 print:max-h-14" />
                  ) : null}
                  {signatureDataUrl ? (
                    <img
                      src={signatureDataUrl}
                      alt=""
                      className="max-h-12 max-w-[4.5rem] object-contain opacity-95 print:max-h-14"
                    />
                  ) : (
                    <div className="h-10 w-full rounded-md border border-dotted border-slate-400 bg-white/40 print:border-slate-600" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {footerNote ? <p className="mt-2 text-center text-[10px] text-slate-500 print:text-slate-700">{footerNote}</p> : null}
    </div>
  );
};

export default ChequeLeafArchive;
